import { provideApolloClient } from "@vue/apollo-composable";
import { computed, ref } from "vue";
import { apolloClient, getPages } from "@/core/api/graphql";
import { useThemeContext } from "@/core/composables/useThemeContext";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import {
  createCareersLocationModel,
  normalizeCareersLocationValue,
} from "@/shared/thorlabs/careers/utils/locationVisibility";
import type { PageType } from "@/core/api/graphql/types";
import type { CareersLocationModelType, ICareersLocationPage } from "@/shared/thorlabs/careers/types";
import type { Ref } from "vue";

interface ICareersLocationsState {
  locations: Ref<CareersLocationModelType[]>;
  isLoading: Ref<boolean>;
  isLoaded: Ref<boolean>;
  error: Ref<unknown>;
  loadingPromise?: Promise<void>;
}

const stateByStoreAndCulture = new Map<string, ICareersLocationsState>();

const createState = (): ICareersLocationsState => ({
  locations: ref([]),
  isLoading: ref(false),
  isLoaded: ref(false),
  error: ref(undefined),
});

const getState = (): ICareersLocationsState => {
  const key = `${globals.storeId}|${globals.cultureName}`;
  const existingState = stateByStoreAndCulture.get(key);

  if (existingState) {
    return existingState;
  }

  const state = createState();
  stateByStoreAndCulture.set(key, state);
  return state;
};

// Established locations are translated via i18n (e.g. "pages.careers.united_kingdom"); CMS Name is the fallback for new ones.
const localizeLocationLabel = (location: CareersLocationModelType): CareersLocationModelType => {
  const slug = location.permalink.split("/").filter(Boolean).pop();
  const translationKey = slug ? `pages.careers.${slug.replace(/-/g, "_")}` : "";
  const i18nGlobal = globals.i18n?.global;

  if (!translationKey || !i18nGlobal?.te(translationKey)) {
    return location;
  }

  return { ...location, label: i18nGlobal.t(translationKey) };
};

const mapPublishedLocationPages = (pages: PageType[]): CareersLocationModelType[] => {
  const locationsByKey = new Map<string, CareersLocationModelType>();

  for (const result of pages) {
    try {
      const page = JSON.parse(result.content) as ICareersLocationPage;
      const location = createCareersLocationModel(page, result.permalink ?? page.contentLink?.url ?? "");

      if (!location) {
        continue;
      }

      if (locationsByKey.has(location.key)) {
        Logger.warn(`Duplicate published Careers location ignored: ${location.country} ${location.state}`);
        continue;
      }

      locationsByKey.set(location.key, location);
    } catch (error) {
      Logger.warn(`Invalid Careers location page ignored: ${result.id}`);
    }
  }

  const locations = [...locationsByKey.values()].map(localizeLocationLabel);
  const hasUnitedStatesParent = locations.some(
    (location) => location.key === normalizeCareersLocationValue("United States"),
  );

  return locations
    .filter(
      (location) =>
        hasUnitedStatesParent ||
        normalizeCareersLocationValue(location.country) !== "united states" ||
        !location.state ||
        normalizeCareersLocationValue(location.state) === normalizeCareersLocationValue("AllUS"),
    )
    .sort(
      (left, right) =>
        left.navigationOrder - right.navigationOrder ||
        left.label.localeCompare(right.label) ||
        left.permalink.localeCompare(right.permalink),
    );
};

export const useCareersLocations = () => {
  const state = getState();
  const { themeContext } = useThemeContext();

  const fetchLocationPages = (cultureName: string): Promise<PageType[]> =>
    getPages({
      storeId: globals.storeId,
      cultureName,
      first: 100,
      keyword: "contenttypes:CareersLocationPage",
    });

  const load = async (force = false): Promise<void> => {
    if (state.loadingPromise) {
      return state.loadingPromise;
    }

    if (state.isLoaded.value && !force) {
      return;
    }

    state.isLoading.value = true;
    state.error.value = undefined;

    state.loadingPromise = provideApolloClient(apolloClient)(async () => {
      try {
        let locations = mapPublishedLocationPages(await fetchLocationPages(globals.cultureName));

        // Location pages may only exist in the store default culture; fall back so the menu never disappears.
        const defaultCultureName = themeContext.value?.defaultLanguage?.cultureName;
        if (!locations.length && defaultCultureName && defaultCultureName !== globals.cultureName) {
          locations = mapPublishedLocationPages(await fetchLocationPages(defaultCultureName));
        }

        state.locations.value = locations;
        state.isLoaded.value = true;
      } catch (error) {
        state.locations.value = [];
        state.error.value = error;
        Logger.error("Failed to load published Careers locations", error);
      } finally {
        state.isLoading.value = false;
        state.loadingPromise = undefined;
      }
    });

    return state.loadingPromise;
  };

  const countryLocations = computed(() =>
    state.locations.value.filter(
      (location) =>
        !location.state || normalizeCareersLocationValue(location.state) === normalizeCareersLocationValue("AllUS"),
    ),
  );
  const unitedStatesLocations = computed(() =>
    state.locations.value.filter(
      (location) =>
        normalizeCareersLocationValue(location.country) === "united states" &&
        Boolean(location.state) &&
        normalizeCareersLocationValue(location.state) !== normalizeCareersLocationValue("AllUS"),
    ),
  );

  const getLocation = (country: string | undefined, stateName: string | undefined) => {
    const countryKey = normalizeCareersLocationValue(country);
    const stateKey = normalizeCareersLocationValue(stateName);

    return state.locations.value.find((location) => {
      if (normalizeCareersLocationValue(location.country) !== countryKey) {
        return false;
      }

      if (countryKey !== "united states") {
        return true;
      }

      return normalizeCareersLocationValue(location.state) === stateKey;
    });
  };

  void load();

  return {
    locations: state.locations,
    countryLocations,
    unitedStatesLocations,
    isLoading: state.isLoading,
    isLoaded: state.isLoaded,
    error: state.error,
    load,
    getLocation,
  };
};
